import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AllPMEIsInStudyQuery_study_StudyNode_procedureRuns_ProcedureRunNode as ProcedureRunNode} from 'studies/typed/process-development/unit-op-linking-flowchart/__generated/AllPMEIsInStudyQuery';
import {
  AllPMEIsInStudyQuery,
  AllPMEIsInStudyQueryVariables,
} from 'studies/typed/process-development/unit-op-linking-flowchart/__generated/AllPMEIsInStudyQuery';
import {ALL_PMEIS_IN_STUDY_QUERY} from 'studies/typed/process-development/unit-op-linking-flowchart/useUnitOpLinkingState';

import {PLANNED} from '../useProcessDevelopmentStudyProgress';
import {UnitOpLinkEdge} from './UnitOpLinkingFlowchartUtils';
import {UnitOperationFields} from './useUnitOpLinkingState';

export enum UnitOpLinkingStudyStateStatus {
  CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART = 'CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART',
  QUERY_ERROR = 'QUERY_ERROR',
  QUERY_PENDING = 'QUERY_PENDING',
  STUDY_INACCESSIBLE = 'STUDY_INACCESSIBLE',
  SUCCESS = 'SUCCESS',
}

export interface SuccessfulQueryUnitOpLinkingStudyState {
  status: UnitOpLinkingStudyStateStatus.SUCCESS;
  studyType: StudyType;
  pmeis: Array<UnitOperationFields>;
  interProcedureLinks: Array<UnitOpLinkEdge>;
}

export interface ContainsExpandedFlowchartUnitOpLinkingStudyState {
  status: UnitOpLinkingStudyStateStatus.CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART;
  studyType: StudyType;
}

export interface QueryErrorUnitOpLinkingStudyState {
  status: Exclude<
    UnitOpLinkingStudyStateStatus,
    | UnitOpLinkingStudyStateStatus.SUCCESS
    | UnitOpLinkingStudyStateStatus.STUDY_INACCESSIBLE
    | UnitOpLinkingStudyStateStatus.CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART
  >;
  inaccessibleStudy?: never;
}

export interface StudyInaccessibleUnitOpLinkingStudyState {
  status: UnitOpLinkingStudyStateStatus.STUDY_INACCESSIBLE;
  inaccessibleStudy: {
    __typename: 'InaccessibleInfoNode';
    invalidId: string;
    canRequestAccess: boolean;
  };
}

export type NotSuccessfulQueryUnitOpLinkingStudyState =
  | QueryErrorUnitOpLinkingStudyState
  | StudyInaccessibleUnitOpLinkingStudyState
  | ContainsExpandedFlowchartUnitOpLinkingStudyState;

/**
 * Fetches data from the study for use by the UnitOpLinkingStudyFlowchartEditor.
 * Similar to useUnitOpLinkingState, but fetches all PMEIs and links in the study
 * and doesn't separate the PMEIs into source and destination.
 */
export default function useUnitOpLinkingStudyState(
  studyId: string
): SuccessfulQueryUnitOpLinkingStudyState | NotSuccessfulQueryUnitOpLinkingStudyState {
  const result = useBenchlingQuery<AllPMEIsInStudyQuery, AllPMEIsInStudyQueryVariables>(
    ALL_PMEIS_IN_STUDY_QUERY,
    {
      variables: {studyId},
      // renderCachedDataWhenRefetching must be false here to avoid use of incomplete data from other queries
      // after successful creation of a new run/experiment (at least until apollo-client is upgraded to 3.10+)
      // See issue "useQuery can unexpectedly return partial data"
      renderCachedDataWhenRefetching: false,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    }
  );

  let orderedPMEIs: Array<UnitOperationFields> = [];
  if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.study)) {
    const procedureRuns = accessibleObjs(result.data.study.procedureRuns);
    if (containsExpandedFlowchartProcedureRun(procedureRuns)) {
      return {
        studyType: result.data.study.studySchema.studyType,
        status: UnitOpLinkingStudyStateStatus.CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART,
      };
    }
    orderedPMEIs = getOrderedPMEIs(procedureRuns);
  }

  if (result.type === QueryResultType.ERROR) {
    return {
      status: UnitOpLinkingStudyStateStatus.QUERY_ERROR,
    };
  } else if (result.type === QueryResultType.PENDING) {
    return {
      status: UnitOpLinkingStudyStateStatus.QUERY_PENDING,
    };
  } else if (isInaccessible(result.data.study)) {
    return {
      status: UnitOpLinkingStudyStateStatus.STUDY_INACCESSIBLE,
      inaccessibleStudy: result.data.study,
    };
  } else {
    // Query was successful and study is accessible.
    return {
      status: UnitOpLinkingStudyStateStatus.SUCCESS,
      studyType: result.data.study.studySchema.studyType,
      pmeis: orderedPMEIs,
      interProcedureLinks: getInterProcedureRunLinks(accessibleObjs(result.data.study.procedureRuns)),
    };
  }
}

function getOrderedPMEIs(procedureRuns: Array<ProcedureRunNode>): Array<UnitOperationFields> {
  return procedureRuns.flatMap((procedureRun, runIndex) => {
    const orderedPMDVIds = procedureRun.currentProcedureFlowchartVersion.nodes.map(({id}) => id);
    return procedureRun.methodExecutionInstances.map((pmei, pmeiIndex) => {
      const methodTask = pmei.methodTask;
      const status = methodTask && !isInaccessible(methodTask) ? methodTask.status.statusType : PLANNED;
      return {
        pmeiId: pmei.id,
        procedureRunId: procedureRun.id,
        procedureRunName: procedureRun.name,
        procedureRunOrderKey: runIndex,
        procedureRunDisplayId: procedureRun.displayId,
        methodName: pmei.planMethodDefinitionVersion.name,
        methodOrderKey: orderedPMDVIds.indexOf(pmei.planMethodDefinitionVersion.id),
        conditionName: pmei.procedureRunConditionReplicate.procedureRunCondition.name,
        conditionOrderKey: pmeiIndex, // Assume PMEIs already correctly ordered w.r.t. conditions
        replicateNumber: pmei.procedureRunConditionReplicate.replicateNumber,
        procedureType: procedureRun.procedureType,
        status,
      };
    });
  });
}

/**
 * Returns true if any of the procedure runs have an expanded flowchart.
 * This is used to determine whether we show the unit op linking study flowchart.
 * Procedure runs with expanded flowcharts cause visualization errors because their
 * non-linear structure breaks the methodOrderKey calculation that relies on linear node ordering.
 */
function containsExpandedFlowchartProcedureRun(procedureRuns: Array<ProcedureRunNode>): boolean {
  return procedureRuns.some((procedureRun) => procedureRun.flowchartIsExpanded);
}

/**
 * Returns the links between PMEIs in different procedure runs.
 * There's no need to check if the PMEIs are in the same study because
 * we assume all unit op instance links are within the same study.
 *
 * @param procedureRuns - The procedure runs to get links for.
 * @returns The links between PMEIs in different procedure runs.
 */
function getInterProcedureRunLinks(procedureRuns: Array<ProcedureRunNode>): Array<UnitOpLinkEdge> {
  const links: Array<UnitOpLinkEdge> = [];
  procedureRuns.forEach((procedureRun) => {
    procedureRun.methodExecutionInstances.forEach((pmei) => {
      pmei.targetProcedureMethodExecutionInstanceAssociations.forEach((association) => {
        if (!isInaccessible(association)) {
          const sourcePmeiId = association.sourceProcedureMethodExecutionInstance.id;
          const targetPmeiId = association.targetProcedureMethodExecutionInstance.id;
          if (sourcePmeiId && targetPmeiId) {
            links.push({sourcePMEIId: sourcePmeiId, destinationPMEIId: targetPmeiId});
          }
        }
      });
    });
  });
  return links;
}
